import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, Eye, EyeOff, AlertCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.name}`);
      if (user.role === 'cliente') {
        navigate('/cliente');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Credenciales inválidas';
      setError(typeof msg === 'string' ? msg : 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-soem-bg">
      {/* Left panel - image */}
      <div
        className="hidden lg:flex w-1/2 relative overflow-hidden"
        style={{ background: '#0D1B2A' }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1664526937033-fe2c11f1be25?w=1200&q=80)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-soem-primary rounded-sm flex items-center justify-center">
              <ShieldCheck size={22} className="text-white" />
            </div>
            <span className="text-white font-black text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>SOEM</span>
          </div>
          <div>
            <h1 className="text-white font-black text-5xl leading-none mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Sistema de<br />Evaluación de<br />Seguridad
            </h1>
            <p className="text-slate-300 text-lg">
              Plataforma profesional para evaluacion y operaciones de seguridad en eventos.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { label: 'Criterios', value: '35' },
                { label: 'Secciones', value: '3' },
                { label: 'Roles', value: '4' },
              ].map((s) => (
                <div key={s.label} className="border border-white/20 rounded-sm p-4 text-center">
                  <div className="text-soem-primary font-black text-3xl" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.value}</div>
                  <div className="text-slate-400 text-xs uppercase tracking-wider mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-soem-primary rounded-sm flex items-center justify-center">
              <ShieldCheck size={22} className="text-white" />
            </div>
            <span className="text-soem-navy font-black text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>SOEM</span>
          </div>

          <div className="soem-card p-8 animate-fade-in">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-soem-navy" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Iniciar sesión
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                Accede con tus credenciales de SOEM
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="soem-label block mb-1.5" htmlFor="email">
                  Correo electrónico
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-soem-primary focus:border-transparent bg-white text-soem-navy text-sm"
                  placeholder="usuario@soem.com"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <label className="soem-label block mb-1.5" htmlFor="password">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-soem-primary focus:border-transparent bg-white text-soem-navy text-sm"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-red-700 text-sm" data-testid="login-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full soem-btn-primary py-3 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Accediendo...
                  </>
                ) : (
                  'Iniciar sesión'
                )}
              </button>

              <div className="text-center">
                <a href="/forgot-password" className="text-soem-primary text-sm hover:underline font-medium">
                  ¿Olvidaste tu contraseña?
                </a>
              </div>

              <div className="flex items-center justify-center mt-2">
                <a
                  href="https://apps.apple.com/mx/app/soem-pg/id6762004834"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-soem-navy transition-colors"
                  data-testid="ios-app-link-login"
                >
                  <Smartphone size={15} />
                  <span>Descargar app iOS</span>
                </a>
              </div>
            </form>

            <p className="text-center text-xs text-slate-400 mt-6">
              SOEM &copy; 2025 — Plataforma de Evaluación de Seguridad
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
